import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Search, FileText, Trash2, Eye, EyeOff, Save, ChevronLeft } from 'lucide-react';
import { useWorkspace } from '../lib/workspace.jsx';
import { api } from '../lib/api.js';
import { usePanels } from '../lib/panels.jsx';

const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

/**
 * Per-app knowledge base. Published articles are what the widget's pre-chat
 * form surfaces, so publishing here is the lever that moves deflection.
 */
export function DocsView() {
  const { activeAppId, allApps, appIndex, scopedApps } = useWorkspace();
  const { mobile } = usePanels();
  // Read inside `load`, which must not be rebuilt (and refetch) on a resize.
  const mobileRef = useRef(mobile);
  mobileRef.current = mobile;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  /**
   * Which app a new article is filed under.
   *
   * A knowledge base belongs to one tenant — the widget reads it by public key
   * — so the all-apps scope can show every article together but still has to
   * ask this one question before creating anything.
   */
  const [target, setTarget] = useState(activeAppId ?? '');

  const scopeKey = useMemo(() => scopedApps.map((a) => a.id).join(','), [scopedApps]);

  useEffect(() => {
    setTarget((current) =>
      current && scopedApps.some((a) => a.id === current) ? current : (scopedApps[0]?.id ?? '')
    );
  }, [scopeKey, scopedApps]);

  /* One request per app in scope, merged; a failing app drops out quietly. */
  const load = useCallback(async () => {
    const appIds = scopeKey ? scopeKey.split(',') : [];
    if (appIds.length === 0) {
      setDocs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const pages = await Promise.all(
        appIds.map((appId) =>
          api.docs
            .list(appId, { limit: 100 })
            .then(({ data }) => (Array.isArray(data) ? data : []))
            .catch(() => [])
        )
      );
      const merged = pages.flat();
      setDocs(merged);
      setActiveId((current) => {
        if (merged.some((d) => d.id === current)) return current;
        // On a phone the editor is a whole screen, so opening the first
        // article for them would bury the list they came to browse.
        return mobileRef.current ? null : (merged[0]?.id ?? null);
      });
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [scopeKey]);

  useEffect(() => {
    load();
  }, [load]);

  const active = useMemo(() => docs.find((d) => d.id === activeId) ?? null, [docs, activeId]);

  useEffect(() => {
    setDraft(active ? { ...active } : null);
    setError(null);
  }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const visible = docs.filter(
      (d) => !needle || `${d.title} ${d.excerpt}`.toLowerCase().includes(needle)
    );
    const map = new Map();
    for (const doc of visible) {
      // Across tenants the app leads the group: two apps both having a
      // "Troubleshooting" section is the normal case, not a collision.
      const category = doc.category || 'UNCATEGORISED';
      const key = allApps
        ? `${appIndex[doc.appId]?.name ?? 'UNKNOWN APP'} · ${category}`
        : category;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(doc);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [docs, query, allApps, appIndex]);

  const create = async () => {
    if (!target) return;
    const title = 'Untitled article';
    const doc = await api.docs.create(target, {
      title,
      slug: `${slugify(title)}-${Date.now().toString(36).slice(-4)}`,
      excerpt: '',
      body: '',
      published: false,
      category: '',
    });
    setDocs((current) => [doc, ...current]);
    setActiveId(doc.id);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      // Written back to the article's own app, whatever the current scope.
      const updated = await api.docs.update(draft.appId, draft.id, {
        title: draft.title,
        slug: draft.slug,
        excerpt: draft.excerpt,
        body: draft.body,
        category: draft.category,
        published: draft.published,
      });
      setDocs((current) => current.map((d) => (d.id === updated.id ? updated : d)));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async () => {
    if (!draft) return;
    const updated = await api.docs.update(draft.appId, draft.id, { published: !draft.published });
    setDocs((current) => current.map((d) => (d.id === updated.id ? updated : d)));
    setDraft((current) => ({ ...current, published: updated.published }));
  };

  const remove = async () => {
    if (!active) return;
    await api.docs.remove(active.appId, active.id);
    setDocs((current) => current.filter((d) => d.id !== active.id));
    setActiveId(null);
  };

  const dirty =
    draft &&
    active &&
    ['title', 'slug', 'excerpt', 'body', 'category'].some((key) => draft[key] !== active[key]);

  return (
    /* Same two-pane-becomes-two-screens rule as the chat: on a phone the list
       and the editor take turns, and this says whose turn it is. */
    <div className={`docs${draft ? ' docs--reading' : ''}`}>
      <aside className="docs__list">
        <header className="docs__head">
          <h1 className="page__title">Docs</h1>
          {/* The one thing a merged list cannot infer: which knowledge base a
              new article joins. Sits beside the button that needs the answer. */}
          {allApps && scopedApps.length > 1 && (
            <select
              className="select docs__target"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              aria-label="App for new articles"
              title="New articles are created in this app"
            >
              {scopedApps.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            className="icon-button"
            onClick={create}
            disabled={!target}
            aria-label="New article"
            title={
              allApps && appIndex[target]
                ? `New article in ${appIndex[target].name}`
                : 'New article'
            }
          >
            <Plus size={13} />
          </button>
        </header>

        <div className="search search--sm docs__search">
          <Search size={12} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles"
            aria-label="Search articles"
          />
        </div>

        <div className="docs__tree">
          {loading && <div className="inbox__empty">Loading…</div>}
          {!loading && docs.length === 0 && (
            <div className="inbox__empty">No articles yet. Create the first one.</div>
          )}
          {grouped.map(([category, items]) => (
            <div key={category}>
              <div className="inbox__group">{category.toUpperCase()}</div>
              {items.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  className={`docs__item${doc.id === activeId ? ' is-active' : ''}`}
                  onClick={() => setActiveId(doc.id)}
                >
                  <FileText size={12} />
                  <span className="ellipsis">{doc.title}</span>
                  {!doc.published && <span className="flag flag--muted">DRAFT</span>}
                </button>
              ))}
            </div>
          ))}
        </div>
      </aside>

      <section className="docs__editor">
        {!draft && <div className="empty">Select an article, or create one.</div>}

        {draft && (
          <>
            <header className="docs__bar">
              {/* Back to the article list. Phone-only, and hidden by CSS
                  everywhere the list is still on screen beside this. */}
              <button
                type="button"
                className="icon-button docs__back"
                onClick={() => setActiveId(null)}
                aria-label="Back to the article list"
                title="Back to the article list"
              >
                <ChevronLeft size={16} />
              </button>
              {/* Whose knowledge base this article is in — the one thing the
                  slug alone cannot tell you once the list is merged. */}
              {allApps && appIndex[draft.appId] && (
                <span
                  className="docs__app"
                  style={{ color: appIndex[draft.appId].color, borderColor: appIndex[draft.appId].color }}
                >
                  {appIndex[draft.appId].name}
                </span>
              )}
              <span className="mono muted">/docs/{draft.slug}</span>
              <span className={`pill ${draft.published ? 'pill--ok' : 'pill--muted'}`}>
                {draft.published ? 'Published' : 'Draft'}
              </span>
              <div className="page__actions">
                <button type="button" className="button" onClick={togglePublish}>
                  {draft.published ? <EyeOff size={13} /> : <Eye size={13} />}
                  {draft.published ? 'Unpublish' : 'Publish'}
                </button>
                <button type="button" className="button" onClick={remove}>
                  <Trash2 size={13} /> Delete
                </button>
                <button
                  type="button"
                  className="button button--primary"
                  onClick={save}
                  disabled={saving || !dirty}
                >
                  <Save size={13} /> {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </header>

            <div className="docs__form">
              {error && <div className="login__error">{error}</div>}

              <input
                className="docs__title"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="Article title"
                aria-label="Title"
              />

              <div className="docs__meta">
                <label className="field field--inline">
                  <span className="field__label">SLUG</span>
                  <input
                    className="field__control mono"
                    value={draft.slug}
                    onChange={(e) => setDraft({ ...draft, slug: slugify(e.target.value) })}
                  />
                </label>
                <label className="field field--inline">
                  <span className="field__label">CATEGORY</span>
                  <input
                    className="field__control"
                    value={draft.category}
                    onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                    placeholder="Troubleshooting"
                  />
                </label>
              </div>

              <label className="field">
                <span className="field__label">EXCERPT · SHOWN IN THE WIDGET</span>
                <textarea
                  className="field__control field__control--area"
                  rows={2}
                  maxLength={500}
                  value={draft.excerpt}
                  onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })}
                />
              </label>

              <label className="field field--grow">
                <span className="field__label">BODY</span>
                <textarea
                  className="field__control field__control--area field__control--grow"
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  placeholder="Write the article…"
                />
              </label>

              <div className="docs__footnote">
                Published articles are readable by the widget through
                <code> GET /widget/docs?publicKey=…</code> and surface in the pre-chat form.
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export default DocsView;
